import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ProxyKeyResponse, ProxyKeysSettings, ZpmPrivilege } from '../../shared/models/generated/ziroModels';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GlobalPropertiesContext } from '../../shared/contexts/global-properties.context';
import { AuthenticationContext } from '../../shared/contexts/authentication.context';

@Injectable()
export class ProxyKeysContext implements OnDestroy {
  private http = inject(HttpClient);
  private _globalPropertiesContext = inject(GlobalPropertiesContext);
  private _authenticationContext = inject(AuthenticationContext);

  private _stateSource = new ReplaySubject<ProxyKeysSettings>(1);
  state$ = this._stateSource.asObservable();

  private _baseUrl = '/services/microsoft/admin/proxy-keys';
  private _subscriptions = new Subscription();

  constructor() {
    const sub = combineLatest([this._globalPropertiesContext.state$, this._authenticationContext.state$]).subscribe(
      ([globalProperties, currentUser]) => {
        if (
          globalProperties.hostedEnabled &&
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.GLOBAL_SETTINGS)
        ) {
          this._init();
        }
      },
    );

    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _init(): void {
    this._get().subscribe((proxyKeySettings) => this._stateSource.next(proxyKeySettings));
  }

  private _get(): Observable<ProxyKeysSettings> {
    return this.http.get<ProxyKeysSettings>(this._baseUrl);
  }

  post(): Observable<ProxyKeyResponse> {
    return this.http.post<ProxyKeyResponse>(this._baseUrl, null).pipe(tap(() => this.refresh()));
  }

  delete(): Observable<void> {
    return this.http.delete<void>(this._baseUrl).pipe(
      tap(() =>
        this._stateSource.next({
          generatedDate: '',
          keyFirstThree: '',
        }),
      ),
    );
  }

  refresh(): void {
    this._init();
  }
}
